import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import EmptyStateCard from 'modules/_shared/components/EmptyStates/EmptyStateCard';
import { TradingOffer, TradingOfferType } from '../../../types/TradingOffer';
import {
  TradingOfferSortOption,
  tradingOffersSortOptions,
} from '../constants/tradingOffersSortOptions';
import TradingOfferRow from './TradingOfferRow';

type TradingOffersSectionProps = {
  type: TradingOfferType;
  offers: TradingOffer[];
  sortBy: TradingOfferSortOption;
  onSortChange: (sortBy: TradingOfferSortOption) => void;
  onViewDetail: (offerId: string) => void;
  societyId: string;
};

const TradingOffersSection: FC<TradingOffersSectionProps> = ({
  type,
  offers,
  sortBy,
  onSortChange,
  onViewDetail,
  societyId,
}) => {
  const { t } = useTranslate();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const isSellOffer = type === 'SELL';
  const title = isSellOffer ? t('SellOffers') : t('BuyOffers');
  const viewAllPath = isSellOffer
    ? `/trading-offers-sell/${societyId}`
    : `/trading-offers-buy/${societyId}`;

  const currentSortLabel = tradingOffersSortOptions.find(
    (opt) => opt.value === sortBy
  )?.label;

  return (
    <div className="nk-block nk-block-lg">
      <div className="nk-block-head-sm">
        <div className="nk-block-head-content d-flex flex-wrap justify-content-between align-items-center g-2">
          <h5 className="nk-block-title mb-0">
            {title} <span className="count text-base">({offers.length})</span>
          </h5>
          <Link to={viewAllPath} className="link link-soft is-disable">
            {t('ViewAll')}
          </Link>
        </div>
      </div>

      <div
        className="nk-block-head-content mb-4 d-flex align-items-center gap-2 flex-wrap"
        style={{ marginTop: '-0.5rem' }}
      >
        <span className="text-soft small">{t('SortBy')}:</span>
        <Dropdown isOpen={dropdownOpen} toggle={toggle}>
          <DropdownToggle
            tag="button"
            className="btn btn-sm btn-outline-light dropdown-toggle"
          >
            {t(currentSortLabel || 'Price')}
          </DropdownToggle>
          <DropdownMenu end>
            {tradingOffersSortOptions.map((option) => (
              <DropdownItem
                key={option.value}
                active={sortBy === option.value}
                onClick={() => onSortChange(option.value)}
              >
                {t(option.label)}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </div>

      {offers.length > 0 ? (
        <div className="tender-offer-list nk-iv-scheme-list">
          {offers.map((offer) => (
            <TradingOfferRow
              key={offer._id}
              offer={offer}
              onViewDetail={onViewDetail}
            />
          ))}
        </div>
      ) : (
        <EmptyStateCard
          mainMessage={isSellOffer ? t('NoSellOffersYet') : t('NoBuyOffersYet')}
          centered
        />
      )}
    </div>
  );
};

export default TradingOffersSection;
